import React, { useEffect } from "react";
import { useAppContext } from "../../../context/MainContext/AppContext";
import useRouter from "../../../hooks/router";
import { Role } from "../../../context/MainContext/Constants";

const ACCENT     = "#00c9a7";
const DARK_BG    = "#111e27";
const NAV_BG     = "#1a3a4a";
const TEXT_MAIN  = "#e8f0f4";
const TEXT_MUTED = "#8fafc4";
const BORDER     = "rgba(255,255,255,0.1)";

const Section = ({ title, children }) => (
    <section className="mb-8">
        <h2 style={{ color: TEXT_MAIN, borderBottom: `1px solid ${BORDER}` }}
            className="text-xl font-semibold pb-3 mb-4">
            {title}
        </h2>
        <div style={{ color: TEXT_MUTED }} className="text-sm leading-relaxed space-y-3">
            {children}
        </div>
    </section>
);

const PrivacyPolicy = () => {
    const { role } = useAppContext();
    const { handleNavigation } = useRouter();

    useEffect(() => {
        if (role) {
            handleNavigation(role === Role.Referee ? "/referee/" : "/assigner/");
        }
    }, [role]);

    return (
        <div style={{ background: DARK_BG, minHeight: "100vh" }} className="font-outfit flex flex-col">
            {/* Nav */}
            <nav style={{ background: NAV_BG }} className="w-full px-6 py-4 flex items-center justify-between shadow-md sticky top-0 z-50">
                <img
                    src="/logo.svg" alt="RefAlign"
                    className="h-9 cursor-pointer"
                    onClick={() => handleNavigation("/")}
                />
                <button
                    onClick={() => handleNavigation("/signup")}
                    style={{ background: ACCENT, color: "#0d2230" }}
                    className="px-4 py-2 rounded-lg text-sm font-semibold hover:opacity-90 transition-all"
                >
                    Get Started Free
                </button>
            </nav>

            {/* Content */}
            <div className="max-w-3xl mx-auto w-full px-6 py-16">
                {/* Hero */}
                <div className="mb-12">
                    <h1 style={{ color: TEXT_MAIN }} className="text-4xl font-bold mb-3">Privacy Policy</h1>
                    <p style={{ color: TEXT_MUTED }} className="text-sm">Last updated December 06, 2023</p>
                </div>

                <div style={{ color: TEXT_MUTED }} className="text-sm leading-relaxed mb-10">
                    This privacy notice for RefAlign Inc. (&quot;we&quot;, &quot;us&quot;, or &quot;our&quot;) describes
                    how and why we might collect, store, use, and/or share your information when you use our services,
                    such as when you visit our website at refalign.com or engage with us in other related ways.
                    Questions or concerns? Email us at{" "}
                    <a href="mailto:sales@refalign.com" style={{ color: ACCENT }}>sales@refalign.com</a>.
                </div>

                <Section title="1. What Information Do We Collect?">
                    <p><strong style={{ color: TEXT_MAIN }}>Personal information you provide:</strong> When you register or use our services, we may collect names, phone numbers, email addresses, usernames, passwords, and payment data.</p>
                    <p><strong style={{ color: TEXT_MAIN }}>Payment data:</strong> Payment processing is handled by Stripe. See their privacy notice at{" "}
                        <a href="https://stripe.com/privacy" style={{ color: ACCENT }} target="_blank" rel="noreferrer">stripe.com/privacy</a>.
                    </p>
                    <p><strong style={{ color: TEXT_MAIN }}>Automatically collected:</strong> We collect device information, IP addresses, browser type, and usage data to maintain the security and operation of our services.</p>
                </Section>

                <Section title="2. How Do We Process Your Information?">
                    <p>We process your information to:</p>
                    <ul className="list-disc pl-5 space-y-1 mt-2">
                        <li>Facilitate account creation and authentication</li>
                        <li>Deliver and improve our services</li>
                        <li>Process payments and manage orders</li>
                        <li>Send administrative and service-related messages</li>
                        <li>Respond to support inquiries</li>
                        <li>Comply with legal obligations</li>
                    </ul>
                </Section>

                <Section title="3. When Do We Share Your Information?">
                    <p>We may share your information in the following situations:</p>
                    <ul className="list-disc pl-5 space-y-1 mt-2">
                        <li><strong style={{ color: TEXT_MAIN }}>Business transfers:</strong> During mergers, acquisitions, or asset sales</li>
                        <li><strong style={{ color: TEXT_MAIN }}>Affiliates:</strong> With subsidiaries or related companies who are bound by this policy</li>
                        <li><strong style={{ color: TEXT_MAIN }}>Business partners:</strong> To offer products, services, or promotions</li>
                    </ul>
                    <p className="mt-3">We have not sold or shared personal information to third parties for commercial purposes in the past 12 months, and we do not intend to do so.</p>
                </Section>

                <Section title="4. Cookies & Tracking">
                    <p>We may use cookies and similar technologies to store information and improve your experience. You can configure your browser to refuse cookies, though some features may be affected.</p>
                </Section>

                <Section title="5. Social Logins">
                    <p>If you register using a social media account (Google, etc.), we receive profile information such as your name, email, and profile photo. We use this only to create and manage your account.</p>
                </Section>

                <Section title="6. Data Retention">
                    <p>We retain personal information only as long as necessary to provide our services or as required by law. When your account is deleted, we remove your data from active systems, subject to backup and legal retention requirements.</p>
                </Section>

                <Section title="7. Data Security">
                    <p>We implement reasonable technical and organizational security measures. However, no internet transmission is 100% secure. Use our services within a secure environment and contact us if you suspect unauthorized access.</p>
                </Section>

                <Section title="8. Children's Privacy">
                    <p>We do not knowingly collect data from anyone under 18. If you believe we have collected such data, contact us at{" "}
                        <a href="mailto:sales@refalign.com" style={{ color: ACCENT }}>sales@refalign.com</a> and we will promptly delete it.
                    </p>
                </Section>

                <Section title="9. Your Privacy Rights">
                    <p>Depending on your location, you may have rights to access, correct, delete, or restrict processing of your personal data. To exercise these rights, contact us at{" "}
                        <a href="mailto:sales@refalign.com" style={{ color: ACCENT }}>sales@refalign.com</a>.
                    </p>
                    <p className="mt-2"><strong style={{ color: TEXT_MAIN }}>California residents (CCPA):</strong> You have the right to know, delete, and opt out of the sale of personal information. We do not sell personal information.</p>
                    <p className="mt-2"><strong style={{ color: TEXT_MAIN }}>Colorado, Connecticut, Utah &amp; Virginia residents:</strong> Additional rights apply under your state&apos;s privacy laws including the right to access, correct, delete, and opt out of data processing for targeted advertising. Contact us to exercise these rights.</p>
                </Section>

                <Section title="10. Do-Not-Track">
                    <p>We do not currently respond to Do-Not-Track browser signals as no uniform standard has been finalized. We will update this policy if that changes.</p>
                </Section>

                <Section title="11. Updates to This Policy">
                    <p>We may update this notice from time to time. The updated version will be effective when posted. We encourage you to review this page periodically.</p>
                </Section>

                <Section title="12. Contact Us">
                    <p>Questions about this privacy policy? Contact us at:</p>
                    <div style={{ background: "rgba(255,255,255,0.04)", border: `1px solid ${BORDER}` }}
                         className="rounded-xl p-4 mt-3">
                        <p style={{ color: TEXT_MAIN }} className="font-medium">RefAlign, Inc.</p>
                        <p>Email: <a href="mailto:sales@refalign.com" style={{ color: ACCENT }}>sales@refalign.com</a></p>
                    </div>
                </Section>
            </div>

            {/* Footer */}
            <footer style={{ background: "#0a1820", borderTop: `1px solid ${BORDER}` }} className="py-6 px-6">
                <div className="max-w-3xl mx-auto flex justify-between items-center">
                    <p style={{ color: TEXT_MUTED }} className="text-xs">© 2026 RefAlign, Inc. All rights reserved.</p>
                    <button
                        onClick={() => handleNavigation("/")}
                        style={{ color: TEXT_MUTED }}
                        className="text-xs hover:text-white transition-colors"
                    >
                        ← Back to home
                    </button>
                </div>
            </footer>
        </div>
    );
};

export default PrivacyPolicy;
